import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { fehlerText, planerApi } from './api';

/* Neuer Plan: leer (feste Vorlage, Standard Fitness) oder aus einer eigenen Vorlage. Eigene Vorlagen lassen sich hier
   auch archivieren. `PersonFeld` = Auswahl einer Person (CRM: Mitgliedersuche, /coach: eigene Athleten). */

function naechsterMontag() {
  const d = new Date();
  const tag = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() + (tag === 0 ? 0 : 7 - tag));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function PlanNeu({ client, basis, onAngelegt, PersonFeld, person: personStart }) {
  const api = useMemo(() => planerApi(client, basis), [client, basis]);
  const [auf, setAuf] = useState(false);
  const [vorlagen, setVorlagen] = useState(null);
  const [f, setF] = useState({ name: '', quelle: 'fest:fitness', startdatum: naechsterMontag(), tage: 3 });
  const [person, setPerson] = useState(personStart || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auf || vorlagen) return;
    api.vorlagen().then((r) => setVorlagen(r.data)).catch((e) => toast.error(fehlerText(e, 'Vorlagen nicht geladen')));
  }, [auf, api, vorlagen]);

  const anlegen = async () => {
    setBusy(true);
    const [art, wert] = f.quelle.split(':');
    const body = { name: f.name.trim(), startdatum: f.startdatum, tage: Number(f.tage) || 1,
                   athleten_ids: person ? [person.id] : [], ...(art === 'fest' ? { vorlage: wert } : { vorlage_id: wert }) };
    try {
      const r = await api.neu(body);
      toast.success('Plan angelegt (Entwurf)');
      setAuf(false); setF((x) => ({ ...x, name: '' }));
      onAngelegt?.(r.data.id);
    } catch (e) { toast.error(fehlerText(e, 'Nicht angelegt')); }
    setBusy(false);
  };
  const archivieren = async (vid) => {
    try { await api.vorlageArchivieren(vid); setVorlagen(null); toast.success('Vorlage archiviert'); } catch (e) { toast.error(fehlerText(e, 'Nicht archiviert')); }
  };

  if (!auf) return <Button className="min-h-[44px] gap-1.5" onClick={() => setAuf(true)} data-testid="plan-neu-oeffnen"><Plus className="h-4 w-4" /> Neuer Plan</Button>;
  const eigen = f.quelle.startsWith('eigen:');
  return (
    <div className="space-y-3 rounded-[14px] border border-border bg-card p-4" data-testid="plan-neu">
      <p className="text-sm font-bold">Neuer Trainingsplan</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs"><span className="text-muted-foreground">Name</span>
          <Input value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="z. B. Kraft 3er-Split" className="h-11" /></label>
        <label className="flex flex-col gap-1 text-xs"><span className="text-muted-foreground">Start (Montag)</span>
          <Input type="date" value={f.startdatum} onChange={(e) => setF({ ...f, startdatum: e.target.value })} className="h-11" /></label>
        <label className="flex flex-col gap-1 text-xs"><span className="text-muted-foreground">Vorlage</span>
          <select aria-label="Vorlage" value={f.quelle} onChange={(e) => setF({ ...f, quelle: e.target.value })} className="h-11 rounded-md border border-input bg-background px-2 text-sm">
            <optgroup label="Leer nach Vorlage">
              {(vorlagen?.fest || [{ key: 'fitness', name: 'Fitness' }]).map((v) => <option key={v.key} value={`fest:${v.key}`}>{v.name} (leer)</option>)}
            </optgroup>
            {vorlagen?.eigene?.length > 0 && <optgroup label="Eigene Vorlagen">{vorlagen.eigene.map((v) => <option key={v.id} value={`eigen:${v.id}`}>{v.name} · {v.tage.length} Tage</option>)}</optgroup>}
          </select></label>
        {!eigen && <label className="flex flex-col gap-1 text-xs"><span className="text-muted-foreground">Tage</span>
          <Input type="number" min={1} max={7} value={f.tage} onChange={(e) => setF({ ...f, tage: e.target.value })} className="h-11" /></label>}
        {PersonFeld && <div className="flex flex-col gap-1 text-xs sm:col-span-2"><span className="text-muted-foreground">Für wen (optional; als Personal Trainer Pflicht)</span>
          <PersonFeld wert={person} onWahl={setPerson} /></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button className="min-h-[44px]" disabled={busy || f.name.trim().length < 2} onClick={anlegen}>Anlegen</Button>
        <Button variant="ghost" className="min-h-[44px]" onClick={() => setAuf(false)}>Abbrechen</Button>
      </div>
      {vorlagen?.eigene?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Eigene Vorlagen verwalten</summary>
          <ul className="mt-2 space-y-1">
            {vorlagen.eigene.map((v) => (
              <li key={v.id} className="flex items-center justify-between gap-2">
                <span className="truncate">{v.name} <span className="text-muted-foreground">· {v.wochen} Wochen · {v.erstellt_von_name || ''}</span></span>
                <Button size="sm" variant="ghost" className="min-h-[44px]" onClick={() => archivieren(v.id)}>Archivieren</Button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
